import { useEffect, useState } from 'react';

const HUBSPOT_BANNER_SELECTOR = '#hs-web-interactives-top-push-anchor';

/**
 * Detects external top banners injected by third-party scripts (e.g. HubSpot)
 * and pins them to the top of the viewport with position:fixed. Returns the
 * banner height so fixed layout elements (header, drawer) can offset below it.
 */
export function useExternalTopBanner(): number {
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const pinAndMeasure = (element: HTMLElement) => {
      element.style.position = 'fixed';
      element.style.top = '0';
      element.style.left = '0';
      element.style.right = '0';
      element.style.zIndex = '1300';
      setBannerHeight(element.offsetHeight);
    };

    const watchElement = (element: HTMLElement) => {
      pinAndMeasure(element);
      resizeObserver = new ResizeObserver(() => setBannerHeight(element.offsetHeight));
      resizeObserver.observe(element);
    };

    const stopWatchingElement = () => {
      setBannerHeight(0);
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const existing = document.querySelector<HTMLElement>(HUBSPOT_BANNER_SELECTOR);
    if (existing) watchElement(existing);

    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && node.matches(HUBSPOT_BANNER_SELECTOR)) {
            watchElement(node);
            return;
          }
        }
        for (const node of mutation.removedNodes) {
          if (node instanceof HTMLElement && node.matches(HUBSPOT_BANNER_SELECTOR)) {
            stopWatchingElement();
            return;
          }
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  return bannerHeight;
}
